import React from 'react';

/* Enhanced formatter for AI-generated insight text.
   - Decodes common HTML entities first.
   - Recognizes category headings (Noteworthy Items, Risks, Trends, Wins, etc.).
   - Supports ordered lists with nested bullet subpoints.
   - Splits bullet lists when a category heading appears so sections don't merge.
   - Avoids over-escaping ampersands (&) so they render correctly.
*/

function decodeBasicEntities(str) {
  return str
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function escapeHtml(str) {
  // Only escape angle brackets to prevent HTML injection; allow & so we don't double-encode it
  return str
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderInline(markup) {
  const parts = [];
  const boldRegex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0; let m;
  while ((m = boldRegex.exec(markup)) !== null) {
    if (m.index > lastIndex) parts.push(escapeHtml(markup.slice(lastIndex, m.index)));
    parts.push(<strong key={parts.length}>{escapeHtml(m[1])}</strong>);
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < markup.length) parts.push(escapeHtml(markup.slice(lastIndex)));
  return parts.map((p, i) => typeof p === 'string' ? <React.Fragment key={i}>{p}</React.Fragment> : p);
}

const bulletLineRegex = /^[-*]\s?/;
const headingPattern = /^\*\*(.+?)\*\*:?$/;              // **Heading** or **Heading:**
const italicHeadingPattern = /^\*(.+?)\*:?$/;               // *Heading* or *Heading:*
const orderedLineRegex = /^(\d+)\.\s+(.+)/;                // 1. Item
const categoryHeadingPattern = /^(Noteworthy Items?|Risks?|Trends?|Wins?|Most Urgent Issues.*|Executive Summary)$/i;

const InsightMarkdown = ({ text }) => {
  if (!text) return null;

  const decoded = decodeBasicEntities(text);
  const lines = decoded.split(/\r?\n/);
  const blocks = [];
  let i = 0;

  const isJunkSeparator = (content) => /^[-*]{1,5}$/.test(content.replace(/\s+/g, '')) || /^(--+|__+|\*\*+)$/.test(content);
  const pushHeading = (txt) => { const clean = txt.replace(/:$/,'').trim(); if (clean) blocks.push({ type: 'heading', text: clean }); };

  while (i < lines.length) {
    let raw = lines[i];
    let line = raw.trim();
    if (!line) { i++; continue; }

    // Ordered list
    if (orderedLineRegex.test(line)) {
      const items = [];
      while (i < lines.length && orderedLineRegex.test(lines[i].trim())) {
        const match = lines[i].trim().match(orderedLineRegex);
        let itemText = match[2].trim();
        i++;
        const subBullets = [];
        while (i < lines.length) {
          const look = lines[i].trim();
            if (!look) { i++; continue; }
            if (orderedLineRegex.test(look)) break;
            if (bulletLineRegex.test(look)) {
              let bulletContent = look.replace(bulletLineRegex, '').trim();
              if (headingPattern.test(bulletContent)) {
                subBullets.push({ heading: bulletContent.replace(headingPattern, '$1') });
              } else if (italicHeadingPattern.test(bulletContent)) {
                subBullets.push({ heading: bulletContent.replace(italicHeadingPattern, '$1') });
              } else if (!isJunkSeparator(bulletContent)) {
                if (categoryHeadingPattern.test(bulletContent.replace(/:$/,''))) {
                  // End current ordered list and treat as heading outside list
                  if (subBullets.length) { /* keep existing */ }
                  pushHeading(bulletContent.replace(/:$/,''));
                } else {
                  subBullets.push(bulletContent);
                }
              }
              i++; continue;
            }
            if (headingPattern.test(look) || italicHeadingPattern.test(look) || orderedLineRegex.test(look)) break;
            itemText += ' ' + look; i++;
        }
        items.push({ text: itemText, sub: subBullets });
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // Bullet list (with category splitting)
    if (bulletLineRegex.test(line)) {
      let items = [];
      while (i < lines.length && bulletLineRegex.test(lines[i].trim())) {
        let content = lines[i].trim().replace(bulletLineRegex, '').trim();
        i++;
        if (!content || isJunkSeparator(content)) continue;
        const isCategory = categoryHeadingPattern.test(content.replace(/:$/,''));
        const isExplicitHeading = headingPattern.test(content) || italicHeadingPattern.test(content);
        if (isCategory || isExplicitHeading) {
          if (items.length) { blocks.push({ type: 'ul', items }); items = []; }
          if (headingPattern.test(content)) pushHeading(content.replace(headingPattern,'$1'));
          else if (italicHeadingPattern.test(content)) pushHeading(content.replace(italicHeadingPattern,'$1'));
          else pushHeading(content.replace(/:$/,''));
          continue;
        }
        items.push(content);
      }
      if (items.length) blocks.push({ type: 'ul', items });
      continue;
    }

    // Standalone headings
    if (headingPattern.test(line)) { pushHeading(line.replace(headingPattern,'$1')); i++; continue; }
    if (italicHeadingPattern.test(line)) { pushHeading(line.replace(italicHeadingPattern,'$1')); i++; continue; }
    if (categoryHeadingPattern.test(line.replace(/:$/,''))) { pushHeading(line.replace(/:$/,'')); i++; continue; }

    // Paragraph accumulation
    const paraLines = [line];
    i++;
    while (i < lines.length) {
      const look = lines[i].trim();
      if (!look) { i++; break; }
      if (bulletLineRegex.test(look) || headingPattern.test(look) || italicHeadingPattern.test(look) || orderedLineRegex.test(look) || categoryHeadingPattern.test(look.replace(/:$/,''))) break;
      paraLines.push(look); i++;
    }
    blocks.push({ type: 'p', text: paraLines.join(' ') });
  }

  return (
    <div className="insight-block">
      {blocks.map((b, idx) => {
        if (b.type === 'heading') return <h4 key={idx}>{renderInline(b.text)}</h4>;
        if (b.type === 'ul') return (
          <ul key={idx}>
            {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
          </ul>
        );
        if (b.type === 'ol') return (
          <ol key={idx}>
            {b.items.map((it, j) => (
              <li key={j}>
                {renderInline(it.text)}
                {it.sub && it.sub.length > 0 && (
                  <ul>
                    {it.sub.map((s, k) => (
                      s && typeof s === 'object' && s.heading ? <li key={k}><strong>{renderInline(s.heading)}</strong></li> : <li key={k}>{renderInline(s)}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        );
        return <p key={idx}>{renderInline(b.text)}</p>;
      })}
    </div>
  );
};

export default InsightMarkdown;
